import { Injectable, signal } from '@angular/core';
import { fetchAuthSession } from 'aws-amplify/auth';
import { SignatureV4 } from '@aws-sdk/signature-v4';
import { Sha256 } from '@aws-crypto/sha256-js';
import { HttpRequest } from '@smithy/protocol-http';

import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../authentication/authentication.service';
import {
  ClientOutboundEvent,
  ConversationTurn,
  initialVoiceAgentState,
  VoiceAgentState
} from './assistant.models';

const AUDIO_SAMPLE_RATE = 16000;
const WORKLET_URL = 'assets/assistant/audio-capture.worklet.js';
// Re-send the user's token well under the ~1h Cognito ID-token lifetime so
// long voice sessions don't break on expiry.
const TOKEN_REFRESH_INTERVAL_MS = 10 * 60 * 1000;

/**
 * Angular service that owns the voice-agent session: the WebSocket connection,
 * microphone capture, audio playback queue, and conversation state.
 *
 * Ported from the React reference (useVoiceAgent.ts + websocket-presigned.ts) in
 * st-general-assistant.
 */
@Injectable({ providedIn: 'root' })
export class AssistantAgentService {
  private readonly _state = signal<VoiceAgentState>({ ...initialVoiceAgentState });
  /** Read-only reactive state for the component. */
  readonly state = this._state.asReadonly();

  private ws: WebSocket | null = null;
  private sessionId: string | null = null;
  private tokenRefreshTimer: ReturnType<typeof setInterval> | null = null;

  constructor(private readonly _auth: AuthService) {}

  // Recording
  private recordingContext: AudioContext | null = null;
  private mediaStream: MediaStream | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;

  // Playback
  private playbackContext: AudioContext | null = null;
  private audioQueue: AudioBuffer[] = [];
  private isPlaying = false;

  private patch(partial: Partial<VoiceAgentState>): void {
    this._state.update(s => ({ ...s, ...partial }));
  }

  // ---- Connection ----

  async connect(): Promise<void> {
    if (this.ws) {
      return;
    }
    try {
      this.patch({ error: null });

      // The agent forwards this token to the JWT-authorized tool APIs, so a
      // session without one is useless — abort rather than open it.
      const token = await this._auth.getJwtToken();
      if (!token) {
        const error = 'Not signed in - cannot start the assistant';
        this.patch({ error });
        throw new Error(error);
      }

      this.sessionId = this.sessionId ?? crypto.randomUUID();
      const url = await this.buildWsUrl(this.sessionId);

      const ws = new WebSocket(url);
      this.ws = ws;

      await new Promise<void>((resolve, reject) => {
        ws.onopen = () => {
          this.patch({ isConnected: true, error: null });
          // Authenticate the session before any audio is sent.
          this.sendAuth(token);
          this.startTokenRefresh();
          resolve();
        };
        ws.onerror = () => {
          const error = 'WebSocket connection failed';
          this.patch({ error });
          reject(new Error(error));
        };
        ws.onclose = () => {
          this.patch({ isConnected: false, isRecording: false, isSpeaking: false });
        };
        ws.onmessage = (event) => this.handleMessage(event);
      });
    } catch (error: any) {
      this.patch({ error: error?.message ?? 'Failed to connect' });
      this.ws = null;
      throw error;
    }
  }

  disconnect(): void {
    this.stopRecording();
    this.stopTokenRefresh();

    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }

    this.audioQueue = [];
    this.isPlaying = false;

    if (this.playbackContext && this.playbackContext.state !== 'closed') {
      this.playbackContext.close();
    }
    this.playbackContext = null;

    // New session next time so context doesn't bleed across sessions.
    this.sessionId = null;
    this._state.set({ ...initialVoiceAgentState });
  }

  clearError(): void {
    this.patch({ error: null });
  }

  private send(message: ClientOutboundEvent): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  /** Send the user's Cognito ID token so the agent can authorize tool calls. */
  private sendAuth(token: string): void {
    this.send({ type: 'auth', token });
  }

  /**
   * Periodically re-send a fresh token. getJwtToken() -> fetchAuthSession()
   * auto-refreshes when the token nears expiry, so this keeps long sessions
   * authenticated. No-ops (and stops) once the socket is gone.
   */
  private startTokenRefresh(): void {
    this.stopTokenRefresh();
    this.tokenRefreshTimer = setInterval(async () => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        this.stopTokenRefresh();
        return;
      }
      try {
        const token = await this._auth.getJwtToken();
        if (token) {
          this.sendAuth(token);
        }
      } catch {
        // Transient refresh failure; the previous token is still valid until expiry.
      }
    }, TOKEN_REFRESH_INTERVAL_MS);
  }

  private stopTokenRefresh(): void {
    if (this.tokenRefreshTimer !== null) {
      clearInterval(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }
  }

  /**
   * Build the WebSocket URL.
   * - Local dev: connect straight to the local agent (no signing).
   * - Deployed: SigV4-presign the AgentCore runtime URL using AWS credentials
   *   vended by the app's existing Cognito Identity Pool (via Amplify).
   */
  private async buildWsUrl(sessionId: string): Promise<string> {
    const cfg = environment.assistant;

    if (cfg.useLocalDev) {
      return cfg.localWsUrl;
    }

    if (!cfg.agentRuntimeArn) {
      throw new Error('Assistant agentRuntimeArn is not configured');
    }

    const credentials = await this.getAwsCredentials();
    const region = cfg.region;
    const encodedArn = encodeURIComponent(cfg.agentRuntimeArn);
    const baseUrl = `https://bedrock-agentcore.${region}.amazonaws.com/runtimes/${encodedArn}/ws`;
    const url = new URL(baseUrl);
    url.searchParams.set('qualifier', 'DEFAULT');
    url.searchParams.set('X-Amzn-Bedrock-AgentCore-Runtime-Session-Id', sessionId);

    const request = new HttpRequest({
      method: 'GET',
      protocol: 'https:',
      hostname: url.hostname,
      path: url.pathname,
      query: Object.fromEntries(url.searchParams),
      headers: { host: url.hostname }
    });

    const signer = new SignatureV4({
      service: 'bedrock-agentcore',
      region,
      credentials,
      sha256: Sha256
    });

    const signed = await signer.presign(request, { expiresIn: 3600 });
    const query = signed.query ?? {};
    const queryString = Object.entries(query)
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
      .join('&');

    return `wss://${signed.hostname}${signed.path || '/'}${queryString ? '?' + queryString : ''}`;
  }

  /** Temporary AWS credentials from the app's Cognito Identity Pool, via Amplify. */
  private async getAwsCredentials(): Promise<{
    accessKeyId: string;
    secretAccessKey: string;
    sessionToken?: string;
  }> {
    const session = await fetchAuthSession();
    const creds = session.credentials;
    if (!creds?.accessKeyId || !creds?.secretAccessKey) {
      throw new Error('No AWS credentials available - please sign in');
    }
    return {
      accessKeyId: creds.accessKeyId,
      secretAccessKey: creds.secretAccessKey,
      sessionToken: creds.sessionToken
    };
  }

  // ---- Incoming messages ----

  private handleMessage(event: MessageEvent): void {
    let data: any;
    try {
      data = JSON.parse(event.data);
    } catch {
      return;
    }

    if (data.type === 'bidi_audio_stream' && data.audio) {
      this.queueAudio(data.audio, data.format || 'pcm', data.sample_rate || AUDIO_SAMPLE_RATE);
    }

    if (data.type === 'bidi_transcript_stream') {
      const role: 'user' | 'assistant' = data.role === 'user' ? 'user' : 'assistant';
      const isFinal = data.is_final !== false;
      this.handleTranscript(data.text || '', isFinal, role);
    }

    if (data.type === 'bidi_text_response' && data.text) {
      this.handleTranscript(data.text, true, 'assistant');
    }

    if (data.type === 'bidi_interruption') {
      this.handleInterruption();
    }
  }

  private handleTranscript(text: string, isFinal: boolean, role: 'user' | 'assistant'): void {
    const prev = this._state();
    const history = [...prev.conversationHistory];

    if (role === 'assistant') {
      if (isFinal && text) {
        const last = history.length ? history[history.length - 1] : null;
        const shouldAppend = last && last.role === 'assistant' && !prev.forceNewBubble;
        if (shouldAppend) {
          const separator = last!.transcript.length > 0 ? ' ' : '';
          history[history.length - 1] = { ...last!, transcript: last!.transcript + separator + text };
        } else {
          history.push({ role: 'assistant', transcript: text, timestamp: new Date() });
        }
        this.patch({
          conversationHistory: history,
          agentTranscript: '',
          isSpeaking: false,
          forceNewBubble: shouldAppend ? prev.forceNewBubble : false
        });
      } else {
        // Partial transcript - accumulate.
        this.patch({ agentTranscript: prev.agentTranscript + text, isSpeaking: true });
      }
    } else {
      if (isFinal && text) {
        history.push({ role: 'user', transcript: text, timestamp: new Date() });
        this.patch({ conversationHistory: history, userTranscript: '' });
      } else {
        this.patch({ userTranscript: prev.userTranscript + text });
      }
    }
  }

  private handleInterruption(): void {
    this.audioQueue = [];
    this.isPlaying = false;
    this.patch({ isSpeaking: false, agentTranscript: '', forceNewBubble: true });
  }

  // ---- Recording ----

  async startRecording(): Promise<void> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: AUDIO_SAMPLE_RATE,
          echoCancellation: true,
          noiseSuppression: true
        }
      });
      this.mediaStream = stream;

      const audioContext = new AudioContext({ sampleRate: AUDIO_SAMPLE_RATE });
      this.recordingContext = audioContext;

      await audioContext.audioWorklet.addModule(WORKLET_URL);

      const source = audioContext.createMediaStreamSource(stream);
      const workletNode = new AudioWorkletNode(audioContext, 'audio-capture-processor');
      this.sourceNode = source;
      this.workletNode = workletNode;

      workletNode.port.onmessage = (event) => {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
          return;
        }
        if (event.data?.type === 'audio') {
          const pcmData = event.data.data as Int16Array;
          const base64Audio = btoa(String.fromCharCode(...new Uint8Array(pcmData.buffer)));
          this.send({
            type: 'bidi_audio_input',
            audio: base64Audio,
            format: 'pcm',
            sample_rate: AUDIO_SAMPLE_RATE,
            channels: 1
          });
        }
      };

      source.connect(workletNode);
      workletNode.connect(audioContext.destination);

      this.patch({ isRecording: true, userTranscript: '' });
    } catch (error: any) {
      this.patch({ error: `Microphone error: ${error?.message ?? error}` });
    }
  }

  stopRecording(): void {
    if (this.workletNode) {
      this.workletNode.port.onmessage = null;
      this.workletNode.disconnect();
      this.workletNode = null;
    }
    if (this.sourceNode) {
      this.sourceNode.disconnect();
      this.sourceNode = null;
    }
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(t => t.stop());
      this.mediaStream = null;
    }
    if (this.recordingContext && this.recordingContext.state !== 'closed') {
      this.recordingContext.close();
    }
    this.recordingContext = null;

    if (this._state().isRecording) {
      this.patch({ isRecording: false });
    }
  }

  // ---- Playback ----

  private queueAudio(base64Audio: string, format: string, sampleRate: number): void {
    try {
      if (!this.playbackContext || this.playbackContext.state === 'closed') {
        this.playbackContext = new AudioContext({ sampleRate });
      }

      let audioBuffer: AudioBuffer;
      if (format === 'pcm') {
        const binaryString = atob(base64Audio);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const pcmData = new Int16Array(bytes.buffer);
        const floatData = new Float32Array(pcmData.length);
        for (let i = 0; i < pcmData.length; i++) {
          floatData[i] = pcmData[i] / (pcmData[i] < 0 ? 0x8000 : 0x7fff);
        }
        audioBuffer = this.playbackContext.createBuffer(1, floatData.length, sampleRate);
        audioBuffer.getChannelData(0).set(floatData);
      } else {
        // Non-PCM formats are not expected from the agent; skip.
        return;
      }

      this.audioQueue.push(audioBuffer);
      if (!this.isPlaying) {
        this.playNext();
      }
    } catch (error) {
      console.error('Error queuing audio:', error);
    }
  }

  private playNext(): void {
    if (this.audioQueue.length === 0) {
      this.isPlaying = false;
      this.patch({ isSpeaking: false });
      return;
    }
    if (!this.playbackContext || this.playbackContext.state === 'closed') {
      this.audioQueue = [];
      this.isPlaying = false;
      this.patch({ isSpeaking: false });
      return;
    }

    this.isPlaying = true;
    this.patch({ isSpeaking: true });

    const audioBuffer = this.audioQueue.shift()!;
    const source = this.playbackContext.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(this.playbackContext.destination);
    source.onended = () => this.playNext();
    source.start();
  }

  /** Convenience: snapshot of conversation for templates. */
  get conversation(): ConversationTurn[] {
    return this._state().conversationHistory;
  }
}
